"use client";

import type { Media } from "@mw/shared/payload-types";

import { DollyZoomAnimated, DollyZoomSlide } from "@/app/_components/DollyZoom";
import { Noise } from "@ui/components/core/Noise/Noise";
import { getImageProps } from "@utilities/payload";
import clsx from "clsx";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import Image from "next/image";
import { useId, useMemo } from "react";
import { useDebounceValue } from "usehooks-ts";
import "./album-music-player.css";

const CornerCircle = ({ className }: { className?: string }) => (
  <div className={clsx("tw-size-1.5 tw-rounded-full tw-bg-white", className)} />
);

interface ProgressBarProps {
  progressBarRef: React.RefObject<HTMLDivElement | null>;
  progressButtonRef: React.RefObject<HTMLButtonElement | null>;
  currentTimeRef: React.RefObject<number>;
  duration: number;
  onSeek: (time: number) => void;
}

const ProgressBar = ({ progressBarRef, progressButtonRef, currentTimeRef, duration, onSeek }: ProgressBarProps) => {
  const handleProgressClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (duration <= 0) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = percentage * duration;
    onSeek(newTime);
  };

  const handleProgressKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (duration <= 0) return;

    let newTime = currentTimeRef.current;

    if (e.key === "ArrowLeft") {
      e.preventDefault();
      newTime = Math.max(0, currentTimeRef.current - 5);
      onSeek(newTime);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      newTime = Math.min(duration, currentTimeRef.current + 5);
      onSeek(newTime);
    }
  };

  return (
    <button
      ref={progressButtonRef}
      className='tw-flex-1 tw-h-1.5 tw-bg-white/30 tw-cursor-pointer tw-rounded-full tw-overflow-hidden tw-border-none tw-p-0 tw-relative focus-visible:tw-outline-focus'
      onClick={handleProgressClick}
      onKeyDown={handleProgressKeyDown}
      role='slider'
      tabIndex={0}
      aria-valuenow={0}
      aria-valuemin={0}
      aria-valuemax={Math.round(duration)}
      aria-label='Seek audio position'
      type='button'
    >
      <div
        ref={progressBarRef}
        className='tw-h-full tw-bg-white tw-pointer-events-none tw-rounded-full tw-absolute tw-inset-y-0 tw-left-0'
        style={{ width: "0%" }}
      />
    </button>
  );
};

const AlbumLabels = () => (
  <div className='tw-absolute tw-inset-5 lg:tw-inset-8 tw-flex tw-items-center tw-pointer-events-none'>
    <div className='tw-w-1/2 tw-relative tw-h-[60px] tw-flex tw-items-center'>
      <span className='amp-animate-label-left tw-absolute tw-text-white/90 tw-uppercase tw-whitespace-nowrap tw-text-2xl tw-tracking-[0.8px]'>
        {"Eleven"}
      </span>
    </div>

    <div className='tw-w-1/2 tw-relative tw-h-[60px] tw-flex tw-items-center'>
      <span className='amp-animate-label-right tw-absolute tw-text-white/90 tw-uppercase tw-whitespace-nowrap tw-text-2xl tw-tracking-[0.8px]'>
        {"Album"}
      </span>
    </div>
  </div>
);

const PlayPauseButton = ({ isPlaying, size = 60 }: { isPlaying: boolean; size?: number }) => {
  const maskUid = useId().replace(/:/g, "");
  const pauseMaskId = `amp-pp-${maskUid}-pause-${size}`;
  const playMaskId = `amp-pp-${maskUid}-play-${size}`;
  const activeMaskId = isPlaying ? pauseMaskId : playMaskId;

  return (
    <svg width={size} height={size} viewBox='0 0 60 60' aria-hidden='true'>
      <defs>
        <mask id={pauseMaskId}>
          <circle cx='30' cy='30' r='30' fill='white' />
          <path
            d='M23.833 23C23.373 23.0002 23.0002 23.373 23 23.833V37.167C23.0002 37.627 23.373 37.9998 23.833 38H27.167C27.627 37.9998 27.9998 37.627 28 37.167V23.833C27.9998 23.373 27.627 23.0002 27.167 23H23.833Z'
            fill='black'
          />
          <path
            d='M32.167 23C31.7069 23 31.3332 23.3729 31.333 23.833V37.167C31.3332 37.6271 31.7069 38 32.167 38H35.5C35.9601 38 36.3328 37.6271 36.333 37.167V23.833C36.3328 23.3729 35.9601 23 35.5 23H32.167Z'
            fill='black'
          />
        </mask>
        <mask id={playMaskId}>
          <circle cx='30' cy='30' r='30' fill='white' />
          <g transform='translate(23.5, 23.25) scale(1.36)'>
            <path
              d='M9.75 4.5333C10.75 5.11065 10.75 6.55402 9.75 7.13137L2.25 11.4615C1.25 12.0389 1.31791e-06 11.3172 1.36838e-06 10.1625L1.74693e-06 1.50221C1.79741e-06 0.347507 1.25 -0.374179 2.25 0.203171L9.75 4.5333Z'
              fill='black'
            />
          </g>
        </mask>
      </defs>
      <circle cx='30' cy='30' r='30' fill='white' mask={`url(#${activeMaskId})`} />
    </svg>
  );
};

interface AlbumArtPlayerProps {
  coverImage: string | Media | null | undefined;
  depthMap?: string | Media | null | undefined;
  isPlaying: boolean;
  currentTimeRef: React.RefObject<number>;
  duration: number;
  progressBarRef: React.RefObject<HTMLDivElement | null>;
  progressButtonRef: React.RefObject<HTMLButtonElement | null>;
  onPlayPause: () => void;
  onSeek: (time: number) => void;
}

export function AlbumArtPlayer({
  coverImage,
  depthMap,
  isPlaying,
  currentTimeRef,
  duration,
  progressBarRef,
  progressButtonRef,
  onPlayPause,
  onSeek,
}: AlbumArtPlayerProps) {
  const [debouncedIsPlaying] = useDebounceValue(isPlaying, 100);
  const imageProps = getImageProps(coverImage, { placeholder: true, fill: true });
  const depthMapProps = getImageProps(depthMap, { placeholder: false, fill: false });
  const imageKey = typeof imageProps?.src === "string" ? imageProps.src : "default";

  // Create DollyZoom slide if both image and depth map are available
  const dollyZoomSlide = useMemo<DollyZoomSlide | null>(() => {
    if (!imageProps?.src || !depthMapProps?.src) return null;

    // getImageProps always returns src as a string, despite the broader ImageProps type
    return {
      imageUrl: imageProps.src as string,
      depthMapUrl: depthMapProps.src as string,
      placeholder: imageProps.blurDataURL || "",
      intensity: 2,
      depthInfluence: 1,
      focusPoint: 0.5,
    };
  }, [imageProps, depthMapProps]);

  const hasDollyZoom = !!dollyZoomSlide;

  return (
    <div className='tw-w-full tw-aspect-square tw-rounded-xl tw-overflow-hidden tw-group tw-relative'>
      {hasDollyZoom ? (
        <div className='tw-absolute tw-inset-0'>
          {imageProps && (
            <img
              alt=''
              src={dollyZoomSlide.placeholder || (imageProps.src as string)}
              className='tw-absolute tw-inset-0 tw-size-full tw-object-cover'
              inert
            />
          )}
          <DollyZoomAnimated slides={[dollyZoomSlide]} selectedIndex={0} isActive />
        </div>
      ) : (
        <AnimatePresence mode='sync' initial={false}>
          {imageProps && (
            <m.div
              key={imageKey}
              className='tw-absolute tw-inset-0'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              <Image {...imageProps} alt='' className='tw-object-cover' priority />
            </m.div>
          )}
        </AnimatePresence>
      )}

      <Noise className='tw-rounded-xl' />

      <CornerCircle className='tw-absolute tw-top-5 lg:tw-top-8 tw-left-5 lg:tw-left-8' />
      <CornerCircle className='tw-absolute tw-top-5 lg:tw-top-8 tw-right-5 lg:tw-right-8' />

      <AlbumLabels />

      <button
        className='amp-animate-play-button tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-transparent tw-border-none tw-cursor-pointer focus-visible:tw-outline-focus'
        onClick={onPlayPause}
        aria-label={isPlaying ? "Pause album" : "Play album"}
        type='button'
      >
        <div className='tw-pointer-events-none'>
          <PlayPauseButton isPlaying={debouncedIsPlaying} />
        </div>
      </button>

      <div className='tw-absolute tw-bottom-5 lg:tw-bottom-8 tw-left-5 lg:tw-left-8 tw-right-5 lg:tw-right-8 tw-flex tw-items-center tw-gap-2.5 tw-z-10'>
        <CornerCircle className='tw-flex-shrink-0' />
        <ProgressBar
          progressBarRef={progressBarRef}
          progressButtonRef={progressButtonRef}
          currentTimeRef={currentTimeRef}
          duration={duration}
          onSeek={onSeek}
        />
        <CornerCircle className='tw-flex-shrink-0' />
      </div>
    </div>
  );
}
